// SSRF vulnerability addressed by implementing a whitelist and validating all fetch requests.
import { AppInfo } from "../types/AppInfo";
import { Communication } from "../types/Communication";
import { Store } from "../types/store";
import { TerritoryRegion } from "../types/territoryRegion";
import { KpiBatchInfoItem, KpiBatchInfoQueryParams } from "../types/kpiBatchInfo";
import { event, eventLogin } from "./gtag";

const whitelist = Object.freeze([
  "/stores",
  "/stores/user",
  "/stores/type",
  "/commercial/turnover",
  "/commercial/objective",
  "/drive/turnover",
  "/commercial/sector",
  "/commercial/turnoverByHour",
  "/commercial/turnoverTriptych",
  "/commercial/turnoverTriptych/allBrands",
  "/commercial/turnoverPromo",
  "/commercial/memberCardTurnover",
  "/commercial/comparative",
  "/commercial/perf-by-store",
  "/ca-live/sdv/turnover",
  "/ca-live/drive/turnover",
  "/ca-live/average-basket",
  "/commercial/turnoverByHourComparison",
  "/nps",
  "/nps/services",
  "/nps/services/types",
  "/nps/barometer",
  "/nps/goodays",
  "/operational/shrinkageByDepartment",
  "/operational/flop5",
  "/operational/shrinkage",
  "/network/token",
  "/operational/markdown",
  "/operational/stockouts",
  "/operational/detention",
  "/operational/detentionTypology",
  "/operational/donationRate",
  "/operational/priceAudit",
  "/operational/price",
  "/operational/recurrences",
  "/operational/vab",
  "/drive/flop",
  "/drive/compliance",
  "/drive/orders",
  "/drive/objective",
  "/drive/slot",
  "/territories-and-regions",
  "/synthesis",
  "/synthesis/alerts/month",
  "/alerts",
  "/management/info",
  "/communications",
  "/batch/info",
  "/batch/kpi-info",
  "/commercial/daysoff",
  "/ca-live/turnoverByHour",
  "/ca-live/sector",
  "/ca-live/perf-by-store"
]);

const local = window?.config?.CREDENTIALS?.LOCAL_MODE;
const BE_URL = window?.config?.CREDENTIALS?.REACT_BE_APP_URL;
const isSaml = !!BE_URL && window.location.href.includes(BE_URL);
const baseUrl = local
  ? window?.config?.CREDENTIALS?.BACKEND_READER_URL
  : (isSaml ? BE_URL : window?.config?.CREDENTIALS?.REACT_APP_URL);

const basePathname = "/reader/api";
const alertsBasePathname = "/alert/api";


const getHeaders = (storeKey?: string, key?: string, countryKey?: string, storeType?: string) => {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(storeKey && { "store-key": storeKey }),
    ...(key && { "key": key }),
    ...(countryKey && { "country-key": countryKey }),
    ...(storeType && { "store-type": storeType }),

  };
};

const getHeadersWithTerritoryKey = (territoryKey?: string) => {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(territoryKey && { "key": territoryKey }),
  };
};

const getHeadersInfo = () => {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
};

const isValidPath = (url: string) => {
  let basePath = basePathname;
  if(url.includes('alerts')){
    basePath = alertsBasePathname;
  }
  try {
    const fullUrl = new URL(url, baseUrl);

    if (fullUrl.origin !== new URL(baseUrl).origin) {
      return false;
    }

    const path = fullUrl.pathname.replace(basePath, "");

    if (!whitelist.includes(path)) {
      return false;
    }

     // Prevent SSRF by disallowing URLs in query parameters
    let isInvalidParam = false;
    fullUrl.searchParams.forEach(value => {
      if (/(?:https?|ftp|file):\/\//.test(value) || value.includes('..')) {
        isInvalidParam = true;
      }
    });
    if (isInvalidParam) {
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};

export const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
};

export const fetcherWithStoreKey = async (
  url: string,
  storeKey: string,
  countryKey?: string
) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl);

  // nosemgrep: typescript.node.security.external-control-of-target-url
  const res = await fetch(fullUrl.toString(), {
    headers: getHeaders(storeKey, undefined, countryKey),
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json();
};

export const fetchCaLiveTurnoverByHour = async (
  url: string,
  countryKey?: string
) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl);

  // nosemgrep: typescript.node.security.external-control-of-target-url
  const res = await fetch(fullUrl.toString(), {
    headers: getHeaders(undefined, countryKey),
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json();
};

export const fetcherWithTerritoryKey = async (
  url: string,
  territoryKey: string
) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl).toString();

  const res = await fetch(fullUrl, {
    headers: getHeaders(undefined, territoryKey),
    credentials: 'include',

  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json();
};

  export const fetchAlertsWithStoreKey = async (
    url: string,
    storeKey: string
  ) => {
    if (!isValidPath(url)) {
      throw new Error("This URL is not allowed");
    }
    const fullUrl = new URL(alertsBasePathname + url, baseUrl).toString();

    // nosemgrep: typescript.node.security.external-control-of-target-url
    const res = await fetch(fullUrl, {
      headers: getHeaders(storeKey),
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Something went wrong with the request: ${res.status}`);
    }

  const data = await res.json();
  return { data, headers: res.headers };
};

export const fetcherWithOnlyToken = async (url: string) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl).toString();

  const res = await fetch(fullUrl, {
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json();
};


export const fetcherWithStoreTypeAndCountryKey = async (
    url: string,
    countryKey?: string,
    storeType?: string

) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl);


  const res = await fetch(fullUrl.toString(), {
    headers: getHeaders(undefined, undefined, countryKey, storeType),
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json();
};

export const postAlertFetcher = async (url: string, body: any) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(alertsBasePathname + url, baseUrl).toString();

  // nosemgrep: typescript.node.security.external-control-of-target-url
  const res = await fetch(fullUrl, {
    method: "POST",
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json();
};

export const fetcherUserStore = async (
  url: string,
  auditTrackingId: string,
  role: string
) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }
  const fullUrl = new URL(basePathname + url, baseUrl).toString();

  const res = await fetch(fullUrl, {
    credentials: 'include',
  });
  
  //tracking first login
  if (auditTrackingId && auditTrackingId?.length) {
    eventLogin();
  }
  //tracking role user
  if (role && role?.length) {
    event({
      action: "user_type",
      value: role,
    });
  }
  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }
  return res.json().then((data) =>  data as Store[]);
};

export const fetcherUserTerritory = async (url: string) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl).toString();

  const res = await fetch(fullUrl, {
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json().then((data) =>  data as TerritoryRegion[]);
};


export const fetchAppVersion = async (): Promise<AppInfo | undefined> => {
  const url = "/management/info";

  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl).toString();

  try {
    const info = await fetch(fullUrl, {
      headers: getHeadersInfo(),
      credentials: 'include',
    });

    if (!info.ok) {
      throw new Error(`An error occured while fetching the request: ${info.status}`);
    }

    const data = await info?.json();

    return {
      version: data?.app?.version ?? "unknown",
      env: data?.app?.env ?? "unknown"
    };
  } catch (error) {
    console.error("Failed to fetch app info", error);
    return undefined;
  }
};


export const fetchCommunications = async (): Promise<Communication [] | undefined> => {
  const url = "/communications";

  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl).toString();

  try {
    const communications = await fetch(fullUrl, {
      headers: getHeadersInfo(),
      credentials: 'include',
    });

    if (!communications.ok) {
      throw new Error(`An error occured while fetching the request: ${communications.status}`);
    }

    const data = await communications?.json();

    return data;
  } catch (error) {
    console.error("Failed to fetch communications", error);
    return undefined;
  }
};


export const saveCommunication = async (communication : Communication): Promise<Communication | undefined> => {
  const url = "/communications";

  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(basePathname + url, baseUrl).toString();

  try {
    const communications = await fetch(fullUrl, {
      headers: getHeadersInfo(),
      credentials: 'include',
      method: "POST",
      body: JSON.stringify(communication)
    });

    if (!communications.ok) {
      throw new Error(`An error occured while saving data: ${communications.status}`);
    }

    const data = await communications?.json();

    return data;
  } catch (error) {
    console.error("Failed to save communication,", error);
    return undefined;
  }
};

export const deleteCommunication = async (id: string) => {
  const url = "/communications";

  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(`${basePathname}${url}/${encodeURIComponent(id)}`, baseUrl).toString();

  try {
    const response = await fetch(fullUrl, {
      method: "DELETE",
      headers: getHeadersInfo(),
      credentials: 'include',
      body: JSON.stringify({ id })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    return true;
  } catch (err) {
    console.error("Failed to delete communication:", err);
    return false;
  }
}

export const fetcherGlobalCommKpiData = async (url: string, key: string) => {
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(`${basePathname}${url}`, baseUrl).toString();

  const response = await fetch(fullUrl, {
    method: "GET",
    headers: getHeadersWithTerritoryKey(key),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return await response.json();
};

export const fetchKpiBatchInfos = async (
  params?: KpiBatchInfoQueryParams
): Promise<KpiBatchInfoItem[]> => {
  const url = "/batch/kpi-info";
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const queryParams = new URLSearchParams();
  if (params?.countryKey) queryParams.append("countryKey", params.countryKey);
  if (params?.kpiName) queryParams.append("kpiName", params.kpiName);
  if (params?.range) queryParams.append("range", params.range);

  const queryString = queryParams.toString();
  const endpoint = queryString ? `${url}?${queryString}` : url;
  const fullUrl = new URL(`${basePathname}${endpoint}`, baseUrl).toString();

  const res = await fetch(fullUrl, {
    method: "GET",
    headers: getHeaders(undefined, undefined, params?.countryKey),
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json();
};

export const fetchKpiBatchInfoById = async (
  id: number
): Promise<KpiBatchInfoItem> => {
  const url = "/batch/kpi-info";
  if (!isValidPath(url)) {
    throw new Error("This URL is not allowed");
  }

  const fullUrl = new URL(`${basePathname}${url}/${encodeURIComponent(id)}`, baseUrl).toString();

  const res = await fetch(fullUrl, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`Something went wrong with the request: ${res.status}`);
  }

  return res.json();
};

 